/**
 * AnimatedTimeline - Animert tidslinje for landing page
 * Visualiserer en oppsigelsesprosess med AML § 15-4 validering
 * @version 1.0.0
 */

import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle, Clock, FileText, Calendar, Shield } from 'lucide-react';

// Tidslinje-data for en typisk oppsigelsesprosess
const TIMELINE_EVENTS = [
  {
    id: 'received',
    date: '15. jan 2026',
    title: 'Oppsigelse mottatt',
    description: 'Formkrav verifisert',
    icon: FileText,
    status: 'complete'
  },
  {
    id: 'notice_period',
    date: '15. jan - 28. feb',
    title: 'Oppsigelsestid',
    description: '1 måned + til månedsslutt',
    icon: Clock,
    status: 'complete'
  },
  {
    id: 'deadline',
    date: '1. mar 2026',
    title: 'Frist utløper',
    description: 'Beregnet automatisk',
    icon: Calendar,
    status: 'active'
  },
  {
    id: 'last_day',
    date: '28. feb 2026',
    title: 'Siste arbeidsdag',
    description: 'Ferdig dokumentert',
    icon: CheckCircle,
    status: 'pending'
  }
];

export function AnimatedTimeline({ compact = false }) {
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const timelineRef = useRef(null);

  // Intersection Observer for viewport detection
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (timelineRef.current) {
      observer.observe(timelineRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  // Animate events sequentially
  useEffect(() => {
    if (!isVisible) return;

    const animateEvents = () => {
      TIMELINE_EVENTS.forEach((_, index) => {
        setTimeout(() => {
          setActiveIndex(index);
        }, index * (compact ? 250 : 400) + 200);
      });
    };

    animateEvents();
  }, [isVisible, compact]);

  // Compact horizontal layout for desktop
  if (compact) {
    return (
      <div ref={timelineRef} className="relative w-full">
        {/* Horizontal timeline */}
        <div className="flex items-center justify-between relative">
          {/* Line */}
          <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-[var(--border-subtle)] -translate-y-1/2">
            <div 
              className="absolute left-0 top-0 h-full bg-gradient-to-r from-[var(--copper)] to-[var(--success)] transition-all duration-700 ease-out"
              style={{ 
                width: isVisible ? `${Math.min((activeIndex + 1) / TIMELINE_EVENTS.length * 100, 100)}%` : '0%',
              }}
            />
          </div>
          
          {/* Events */}
          {TIMELINE_EVENTS.map((event, index) => {
            const Icon = event.icon;
            const isAnimated = index <= activeIndex;
            const isActive = event.status === 'active';
            
            return (
              <div 
                key={event.id}
                className={`
                  relative flex flex-col items-center z-10
                  transition-all duration-300 ease-out
                  ${isAnimated ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}
                `}
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                {/* Icon */}
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center
                  transition-all duration-300
                  ${isActive 
                    ? 'bg-[var(--copper)] shadow-md shadow-[var(--copper)]/30' 
                    : event.status === 'complete'
                      ? 'bg-[var(--success)]/20 border border-[var(--success)]'
                      : 'bg-[var(--bg-highlight)] border border-[var(--border-subtle)]'
                  }
                `}>
                  <Icon className={`w-3.5 h-3.5 ${
                    isActive ? 'text-white' : event.status === 'complete' ? 'text-[var(--success)]' : 'text-[var(--text-muted)]'
                  }`} />
                </div>
                
                {/* Label */}
                <div className="mt-2 text-center max-w-[80px]">
                  <span className="text-[9px] text-[var(--copper)] font-mono block">{event.date.split(' ')[0]}</span>
                  <span className={`text-[10px] font-medium block leading-tight ${isActive ? 'text-[var(--copper)]' : 'text-[var(--text-main)]'}`}>
                    {event.title}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={timelineRef}
      className="relative w-full max-w-2xl mx-auto"
    >
      {/* Timeline container */}
      <div className="relative">
        {/* Animated line */}
        <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 bg-[var(--border-subtle)]">
          <div 
            className="absolute top-0 left-0 w-full bg-gradient-to-b from-[var(--copper)] to-[var(--success)] transition-all duration-1000 ease-out"
            style={{ 
              height: isVisible ? `${Math.min((activeIndex + 1) / TIMELINE_EVENTS.length * 100, 100)}%` : '0%',
              opacity: isVisible ? 1 : 0
            }}
          />
        </div>

        {/* Events */}
        <div className="space-y-6">
          {TIMELINE_EVENTS.map((event, index) => {
            const Icon = event.icon;
            const isAnimated = index <= activeIndex;
            const isActive = event.status === 'active';
            
            return (
              <div 
                key={event.id}
                className={`
                  relative flex items-start gap-4 md:gap-8
                  transition-all duration-500 ease-out
                  ${isAnimated ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}
                `}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* Desktop: Alternating layout */}
                <div className={`
                  hidden md:block flex-1 text-right
                  ${index % 2 === 0 ? 'order-1' : 'order-3 text-left'}
                `}>
                  <span className="text-xs text-[var(--copper)] font-mono tracking-wide">
                    {event.date}
                  </span>
                </div>

                {/* Icon node */}
                <div className={`
                  relative z-10 flex-shrink-0 w-12 h-12 rounded-full 
                  flex items-center justify-center
                  transition-all duration-300
                  md:order-2
                  ${isActive 
                    ? 'bg-[var(--copper)] shadow-lg shadow-[var(--copper)]/30 scale-110' 
                    : event.status === 'complete'
                      ? 'bg-[var(--success)]/20 border-2 border-[var(--success)]'
                      : 'bg-[var(--bg-highlight)] border-2 border-[var(--border-subtle)]'
                  }
                `}>
                  <Icon className={`w-5 h-5 ${
                    isActive 
                      ? 'text-white' 
                      : event.status === 'complete'
                        ? 'text-[var(--success)]'
                        : 'text-[var(--text-muted)]'
                  }`} />
                  
                  {/* Pulse animation for active */}
                  {isActive && isAnimated && (
                    <span className="absolute inset-0 rounded-full bg-[var(--copper)] animate-ping opacity-25" />
                  )}
                </div>

                {/* Content */}
                <div className={`
                  flex-1 pb-2
                  ${index % 2 === 0 ? 'md:order-3 md:text-left' : 'md:order-1 md:text-right'}
                `}>
                  {/* Mobile date */}
                  <span className="md:hidden text-xs text-[var(--copper)] font-mono tracking-wide block mb-1">
                    {event.date}
                  </span>
                  
                  <h4 className={`font-semibold text-sm ${
                    isActive ? 'text-[var(--copper)]' : 'text-[var(--text-main)]'
                  }`}>
                    {event.title}
                  </h4>
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">
                    {event.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Trust seal component
export function TrustSeal() {
  const [isVisible, setIsVisible] = useState(false);
  const sealRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), 800);
        }
      },
      { threshold: 0.5 }
    );

    if (sealRef.current) {
      observer.observe(sealRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div 
      ref={sealRef}
      className={`
        inline-flex items-center gap-3 px-5 py-3 
        bg-[var(--success)]/10 border-2 border-[var(--success)]/40 
        rounded-xl backdrop-blur-sm
        transition-all duration-700 ease-out
        ${isVisible 
          ? 'opacity-100 scale-100 translate-y-0' 
          : 'opacity-0 scale-90 translate-y-4'
        }
      `}
    >
      {/* Shield icon */}
      <div className="relative">
        <Shield className="w-8 h-8 text-[var(--success)]" />
        <CheckCircle className="absolute -bottom-1 -right-1 w-4 h-4 text-[var(--success)] bg-[var(--bg-deep)] rounded-full" />
      </div>
      
      {/* Text */}
      <div className="text-left">
        <span className="block text-xs text-[var(--success)] font-semibold tracking-wide uppercase">
          AML § 15-4 Verifisert
        </span>
        <span className="block text-[10px] text-[var(--text-muted)]">
          Formkrav automatisk kontrollert
        </span>
      </div>
    </div>
  );
}

export default AnimatedTimeline;
